import { useState } from 'react';
import { 
  Globe, 
  Users, 
  Monitor, 
  Smartphone, 
  Tablet, 
  Activity, 
  TrendingUp, 
  Eye,
  BarChart3,
  Clock
} from 'lucide-react';
import InteractiveWorldMap from './InteractiveWorldMap';
import { Button } from '@/components/ui/button';

interface AnalyticsData {
  summary: {
    totalUsers: number;
    totalSessions: number;
    avgSessionDuration: string;
    newUsers: number;
    liveUsers: number;
  };
  locations: Array<{
    country: string;
    region: string;
    city: string;
    users: number;
    sessions: number;
  }>;
  liveLocations: Array<{
    country: string;
    device: string;
    activeUsers: number;
  }>;
  devices: Array<{
    category: string;
    users: number;
    sessions: number;
  }>;
  browsers: Array<{
    name: string;
    users: number;
    sessions: number;
  }>;
  operatingSystems: Array<{
    name: string;
    users: number;
    sessions: number;
  }>;
  trafficSources: Array<{
    source: string;
    medium: string;
    sessions: number;
    users: number;
    label: string;
  }>;
  events: Array<{
    name: string;
    count: number;
  }>;
  lastUpdated: string;
}

interface EnhancedAnalyticsProps {
  analyticsData: AnalyticsData | null;
  onRefresh: () => void;
  loading: boolean;
}

export default function EnhancedAnalytics({ analyticsData, onRefresh, loading }: EnhancedAnalyticsProps) {
  const [selectedTab, setSelectedTab] = useState('overview');

  if (!analyticsData) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No analytics data available</p>
        <Button 
          onClick={onRefresh}
          disabled={loading}
          className="bg-gradient-to-r from-[#0A74FF] to-[#FFD700]"
        >
          {loading ? 'Loading...' : 'Load Analytics'}
        </Button>
      </div>
    );
  }

  const { summary, locations, liveLocations, devices, browsers, operatingSystems, trafficSources, events } = analyticsData;

  // Get device icon
  const getDeviceIcon = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'mobile': return <Smartphone size={16} />;
      case 'tablet': return <Tablet size={16} />;
      case 'desktop': return <Monitor size={16} />;
      default: return <Monitor size={16} />;
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <TrendingUp size={20} />, color: '#0A74FF' },
    { id: 'geography', label: 'Geography', icon: <Globe size={20} />, color: '#22c55e' },
    { id: 'technology', label: 'Technology', icon: <Monitor size={20} />, color: '#a855f7' },
    { id: 'traffic', label: 'Traffic Sources', icon: <Activity size={20} />, color: '#f59e0b' },
    { id: 'realtime', label: 'Real-time', icon: <Eye size={20} />, color: '#ef4444' }
  ];

  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      {/* Header with Live Users and Refresh */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold">Analytics Dashboard</h3>
        <div className="flex items-center gap-4">
          {/* Live Users Indicator */}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
            summary.liveUsers > 0 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-muted border-border'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              summary.liveUsers > 0 ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'
            }`} />
            <Users size={14} className={summary.liveUsers > 0 ? 'text-green-500' : 'text-muted-foreground'} />
            <span className={`text-sm font-semibold ${
              summary.liveUsers > 0 ? 'text-green-500' : 'text-muted-foreground'
            }`}>
              {summary.liveUsers} Live
            </span>
          </div>
          
          <Button 
            onClick={onRefresh}
            disabled={loading}
            size="sm"
            className="bg-gradient-to-r from-[#0A74FF] to-[#FFD700]"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
              selectedTab === tab.id
                ? 'bg-gradient-to-r from-[#0A74FF] to-[#FFD700] text-white'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            {tab.icon}
            <span className="text-sm font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {selectedTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl p-6 border border-blue-500/20">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-blue-500" />
                <span className="text-xs text-muted-foreground">30 days</span>
              </div>
              <div className="text-3xl font-bold mb-1">{summary.totalUsers.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Total Users</div>
              <div className="text-xs text-green-500 mt-2">+{summary.newUsers} new</div>
            </div>

            <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-8 h-8 text-purple-500" />
                <span className="text-xs text-muted-foreground">30 days</span>
              </div>
              <div className="text-3xl font-bold mb-1">{summary.totalSessions.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Total Sessions</div>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 rounded-xl p-6 border border-green-500/20">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-8 h-8 text-green-500" />
                <span className="text-xs text-muted-foreground">Average</span>
              </div>
              <div className="text-3xl font-bold mb-1">{summary.avgSessionDuration}</div>
              <div className="text-sm text-muted-foreground">Avg Session</div>
            </div>

            <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-xl p-6 border border-red-500/20">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-8 h-8 text-red-500" />
                <span className="text-xs text-muted-foreground">Right now</span>
              </div>
              <div className="text-3xl font-bold mb-1">{summary.liveUsers}</div>
              <div className="text-sm text-muted-foreground">Live Users</div>
            </div>
          </div>

          {/* Top Events */}
          <div className="bg-muted/50 rounded-xl p-6 border border-border">
            <h4 className="text-lg font-semibold mb-4">Top Events (30 days)</h4>
            <div className="space-y-2">
              {events.slice(0, 10).map((event, index) => (
                <div key={event.name} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                    <span className="text-sm font-medium">{event.name}</span>
                  </div>
                  <span className="text-sm font-bold">{event.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Geography Tab */}
      {selectedTab === 'geography' && (
        <InteractiveWorldMap locations={locations} />
      )}

      {/* Technology Tab */}
      {selectedTab === 'technology' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Devices */}
          <div className="bg-muted/50 rounded-xl p-6 border border-border">
            <h4 className="text-lg font-semibold mb-4">Devices</h4>
            <div className="space-y-3">
              {devices.map((device) => (
                <div key={device.category} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getDeviceIcon(device.category)}
                    <span className="text-sm capitalize">{device.category}</span>
                  </div>
                  <span className="text-sm font-semibold">{device.users.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Browsers */}
          <div className="bg-muted/50 rounded-xl p-6 border border-border">
            <h4 className="text-lg font-semibold mb-4">Browsers</h4>
            <div className="space-y-3">
              {browsers.slice(0, 8).map((browser) => (
                <div key={browser.name} className="flex items-center justify-between">
                  <span className="text-sm">{browser.name}</span>
                  <span className="text-sm font-semibold">{browser.users.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Operating Systems */}
          <div className="bg-muted/50 rounded-xl p-6 border border-border">
            <h4 className="text-lg font-semibold mb-4">Operating Systems</h4>
            <div className="space-y-3">
              {operatingSystems.slice(0, 8).map((os) => (
                <div key={os.name} className="flex items-center justify-between">
                  <span className="text-sm">{os.name}</span>
                  <span className="text-sm font-semibold">{os.users.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Traffic Sources Tab */}
      {selectedTab === 'traffic' && (
        <div className="bg-muted/50 rounded-xl p-6 border border-border">
          <h4 className="text-lg font-semibold mb-4">Traffic Sources (7 days)</h4>
          <div className="space-y-3">
            {trafficSources.map((source, index) => (
              <div key={`${source.source}-${source.medium}-${index}`} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                <div>
                  <div className="font-medium">{source.label}</div>
                  <div className="text-xs text-muted-foreground">{source.medium}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{source.sessions.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{source.users.toLocaleString()} users</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Real-time Tab */}
      {selectedTab === 'realtime' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-xl p-6 border border-red-500/20">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-red-500" />
              Active Users Right Now
            </h4>
            <div className="text-5xl font-bold mb-2">{summary.liveUsers}</div>
            <div className="text-sm text-muted-foreground">Users currently on your site</div>
          </div>

          {liveLocations.length > 0 && (
            <div className="bg-muted/50 rounded-xl p-6 border border-border">
              <h4 className="text-lg font-semibold mb-4">Live User Locations</h4>
              <div className="space-y-3">
                {liveLocations.map((loc, index) => (
                  <div key={`${loc.country}-${loc.device}-${index}`} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-3">
                      {getDeviceIcon(loc.device)}
                      <span className="text-sm">{loc.country}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-sm font-semibold">{loc.activeUsers}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {liveLocations.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No active users at the moment
            </div>
          )}
        </div>
      )}

      {/* Last Updated */}
      <div className="mt-6 text-xs text-muted-foreground text-center">
        Last updated: {new Date(analyticsData.lastUpdated).toLocaleString()}
      </div>
    </div>
  );
}
